export function getContrastColor(hex: string): "black" | "white" {
  hex = hex.replace(/^#/, "");

  if (hex.length === 3) {
    hex = hex.split("").map(c => c + c).join("");
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? "black" : "white";
}

/**
 * Converts a hexadecimal color code to an RGBA string.
 * The function takes a hexadecimal color code and an alpha value,
 * and converts the color components from hex to decimal values while
 * including the alpha transparency in the final RGBA string.
 */
export function hexToStringRgba(hex: string, alpha: number) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  return "rgba(255, 255, 255, 1)"
}

/**
 * Converts a p-value into a corresponding alpha value based on specified bounds and a power factor.
 * This function clamps the p-value to a maximum of 0.05 and computes the alpha value using a power-based interpolation.
 */
export function pvalueToAlphaPow(pvalue: number, minAlpha = 0.2, maxAlpha = 1, power = 2) {
  const clamped = Math.max(0, Math.min(0.05, pvalue));
  const t = 1 - clamped / 0.05;
  return minAlpha + (maxAlpha - minAlpha) * Math.pow(t, power);
}

/**
 * Converts a p-value to an alpha value using a logarithmic scale.
 * The p-value is clamped between a minimum and maximum range before the conversion.
 */
export function pvalueToAlphaLog(pvalue: number, minAlpha = 0.2, maxAlpha = 1) {
  const pMin = 1e-8; // smallest expected p-value
  const pMax = 0.05;

  // clamp pvalue to [pMin, pMax]
  const clamped = Math.min(Math.max(pvalue, pMin), pMax);

  return minAlpha +
    (maxAlpha - minAlpha) *
    (Math.log10(pMax) - Math.log10(clamped)) /
    (Math.log10(pMax) - Math.log10(pMin));
}

export function darkenHexColor(hex: string, amount: number): string {
  let color = hex.startsWith("#") ? hex.slice(1) : hex;

  if (color.length === 3) {
    color = color.split("").map(c => c + c).join("");
  }

  const num = parseInt(color, 16);
  const r = Math.max(((num >> 16) & 0xff) * (1 - amount), 0);
  const g = Math.max(((num >> 8) & 0xff) * (1 - amount), 0);
  const b = Math.max((num & 0xff) * (1 - amount), 0);

  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

export function lightenHexColor(hex: string, amount: number): string {
  let color = hex.startsWith("#") ? hex.slice(1) : hex;

  if (color.length === 3) {
    color = color.split("").map(char => char + char).join("");
  }

  const num = parseInt(color, 16);
  const r = Math.min(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount, 255);
  const g = Math.min(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount, 255);
  const b = Math.min((num & 0xff) + (255 - (num & 0xff)) * amount, 255);

  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}
